const { prefectures, deliveryTimes, } = require('../config');
const { parse: parseDate } = require('date-fns');
const { sumBy, invert, mapValues } = require('lodash');
const { splitAddress } = require('../splitAddress');

const { entries } = Object;

// 逆引きマップを作成
const prefecturesByName = invert(prefectures);
// 逆引きマップを作成
const deliveryTimesByLabel = {
  ...invert(mapValues(deliveryTimes, 'label')),
  ...invert(mapValues(deliveryTimes, 'lions')),
};

const orderImportFields = () => {
  return {
    id: {
      label: '受注番号',
      type: 'string',
    },
    deliveryDate: {
      label: '配送希望日',
      type: 'date',
    },
    deliveryTime: {
      label: '配送希望時間帯',
      type: 'string',
    },
    createdAt: {
      label: '注文日',
      type: 'date',
    },
    productCodes: {
      label: '商品コード',
      type: 'string',
      importPreview: (row) => row.orderItems?.map(_ => _.productId).join(', ') || '',
    },
    quantities: {
      label: '数量',
      type: 'string',
      importPreview: (row) => row.orderItems?.map(_ => _.quantity).join(', ') || '',
    },
    name: {
      label: '注文者名',
      type: 'string',
    },
    email: {
      label: 'メールアドレス',
      type: 'string',
    },
    phone: {
      label: '電話番号',
      type: 'string',
    },
    postalCode: {
      label: '郵便番号',
      type: 'string',
    },
    prefecture: {
      label: '都道府県',
      type: 'string',
    },
    city: {
      label: '市区町村',
      type: 'string',
    },
    address: {
      label: '住所',
      type: 'string',
    },
    destinationName: {
      label: '配送先名',
      type: 'string',
    },
    destinationPhone: {
      label: '配送先電話番号',
      type: 'string',
    },
    destinationPostalCode: {
      label: '配送先郵便番号',
      type: 'string',
    },
    destinationPrefecture: {
      label: '配送先都道府県',
      type: 'string',
    },
    destinationCity: {
      label: '配送先市区町村',
      type: 'string',
    },
    destinationAddress: {
      label: '配送先住所',
      type: 'string',
    },
    contactorName: {
      label: '利用者名',
      type: 'string',
    },
    contactorPhone: {
      label: '利用者電話番号',
      type: 'string',
    },
    contactorPostalCode: {
      label: '利用者郵便番号',
      type: 'string',
    },
    contactorPrefecture: {
      label: '利用者都道府県',
      type: 'string',
    },
    contactorCity: {
      label: '利用者市区町村',
      type: 'string',
    },
    contactorAddress: {
      label: '利用者住所',
      type: 'string',
    },
    route: {
      label: '流入経路',
      type: 'string',
    },
    note: {
      label: '備考',
      type: 'string',
    },
  };
};

const mapOrderImportRow = (row, productsByCode = {}) => {
  const { 
    id, 
    name,
    email,
    phone,
    postalCode,
    prefecture,
    city,
    address,
    destinationName,
    destinationPhone,
    destinationPostalCode,
    destinationPrefecture,
    destinationCity,
    destinationAddress,
    contactorName,
    contactorPhone,
    contactorPostalCode,
    contactorPrefecture,
    contactorCity,
    contactorAddress,
    deliveryDate: deliveryDateString,
    deliveryTime,
    createdAt: createdAtString,
    productCodes,
    quantities,
    ...rest
  } = row;

  // 都道府県名から都道府県コードを取得
  const prefectureCode = prefecturesByName[prefecture];
  const destinationPrefectureCode = prefecturesByName[destinationPrefecture];
  const contactorPrefectureCode = prefecturesByName[contactorPrefecture];

  // 配送時間帯ラベルから配送時間帯キーを取得
  const deliveryTimeKey = deliveryTimesByLabel[deliveryTime];

  const createdAt = createdAtString ? parseDate(createdAtString, 'yyyy/MM/dd', new Date()) : new Date();
  const deliveryDate = deliveryDateString ? parseDate(deliveryDateString, 'yyyy/MM/dd', new Date()) : null;

  // 商品コードと数量をカンマ区切りで解析
  const productCodeList = productCodes ? productCodes.split(',').map(_ => _.trim()).filter(_ => _) : [];
  const quantityList = quantities ? quantities.split(',').map(_ => parseInt(_.trim(), 10)).filter(_ => !isNaN(_)) : [];

  // orderItemsを生成（商品コードと数量、価格のセット）
  const orderItems = productCodeList.map((productCode, index) => {
    const product = productsByCode[productCode];
    return {
      productId: product?.id,
      quantity: quantityList[index] || 1, // 数量がない場合はデフォルト1
      price: product?.price || 0, // 商品が見つからない場合は0
    };
  }).filter(_ => _.productId); // 商品コードがある行のみ

  // orderItemsから合計金額を計算
  const calculatedAmount = sumBy(orderItems, _ => _.price * _.quantity);

  return {
    id,
    name,
    email,
    phone,
    postalCode: postalCode?.replace('-', ''),
    prefecture: prefectureCode,
    city,
    address,
    destinationName,
    destinationPhone,
    destinationPostalCode: destinationPostalCode?.replace('-', ''),
    destinationPrefecture: destinationPrefectureCode,
    destinationCity,
    destinationAddress,
    contactorName,
    contactorPhone,
    contactorPostalCode: contactorPostalCode?.replace('-', ''),
    contactorPrefecture: contactorPrefectureCode,
    contactorCity,
    contactorAddress,
    deliveryDate,
    deliveryTime: deliveryTimeKey,
    amount: calculatedAmount,
    shipmentFee: 0,
    discountAmount: 0,
    createdAt,
    orderItems,
    productIds: orderItems.map(_ => _.productId),
  };
};

const lionsOrderImportFields = () => {
  return {
    id: {
      label: '受注番号',
      type: 'string',
    },
    deliveryDate: {
      label: '配達指定日',
      type: 'date',
    },
    deliveryTime: {
      label: '配達指定時間',
      type: 'string',
    },
    createdAt: {
      label: '受注日',
      type: 'date',
    },
    productCodes: {
      label: '商品コード',
      type: 'string',
    },
    quantities: {
      label: '受注数',
      type: 'string',
    },
    name: {
      label: '購入者名',
      type: 'string',
    },
    email: {
      label: '購入者メールアドレス',
      type: 'string',
    },
    phone: {
      label: '購入者電話番号',
      type: 'string',
    },
    postalCode: {
      label: '購入者郵便番号',
      type: 'string',
    },
    address: {
      label: '購入者住所1',
      type: 'string',
    },
    address2: {
      label: '購入者住所2',
      type: 'string',
    },
    destinationName: {
      label: '送り先名',
      type: 'string',
    },
    destinationPhone: {
      label: '送り先電話番号',
      type: 'string',
    },
    destinationPostalCode: {
      label: '送り先郵便番号',
      type: 'string',
    },
    destinationAddress: {
      label: '送り先住所1',
      type: 'string',
    },
    destinationAddress2: {
      label: '送り先住所2',
      type: 'string',
    },
    amount: {
      label: '総合計',
      type: 'number',
    },
    slipNumber: {
      label: '伝票番号',
      type: 'string',
    },
  };
};

const mapLionsOrderImportRow = (row, productsByCode = {}) => {
  const { 
    id, 
    name,
    email,
    phone,
    postalCode,
    address,
    address2,
    destinationName,
    destinationPhone,
    destinationPostalCode,
    destinationAddress,
    destinationAddress2,
    deliveryDate: deliveryDateString,
    deliveryTime,
    createdAt: createdAtString,
    productCodes,
    quantities,
    slipNumber,
    ...rest
  } = row;

  // 固定商品コード HBC4-LSA を使用
  const fixedProductCode = 'HBC4-LSA';
  const product = productsByCode[fixedProductCode];

  // 住所1+住所2を結合してsplitAddressで分割
  const buyerFullAddress = [address, address2].filter(_ => _).join('');
  const destinationFullAddress = [destinationAddress, destinationAddress2].filter(_ => _).join('');
  
  const buyerAddressParts = splitAddress(buyerFullAddress);
  const destinationAddressParts = splitAddress(destinationFullAddress);

  // 都道府県名から都道府県コードを取得
  const prefectureCode = prefecturesByName[buyerAddressParts.prefecture];
  const destinationPrefectureCode = prefecturesByName[destinationAddressParts.prefecture];

  // 配送時間帯ラベルから配送時間帯キーを取得
  const deliveryTimeKey = deliveryTimesByLabel[deliveryTime];
  console.log({ deliveryTimesByLabel, deliveryTime });

  const createdAt = createdAtString ? parseDate(createdAtString, 'yyyy-MM-dd', new Date()) : new Date();
  const deliveryDate = deliveryDateString ? parseDate(deliveryDateString, 'yyyy-MM-dd', new Date()) : null;

  // orderItemsを生成（固定商品コードHBC4-LSAを使用）
  const orderItems = [{
    productId: product?.id,
    quantity: parseInt(quantities, 10) || 1,
    price: product?.price || 0,
  }].filter(_ => _.productId);

  // orderItemsから合計金額を計算
  const calculatedAmount = sumBy(orderItems, _ => _.price * _.quantity);

  return {
    id,
    name,
    email,
    phone,
    postalCode: postalCode?.replace('-', ''),
    prefecture: prefectureCode,
    city: buyerAddressParts.city,
    address: buyerAddressParts.rest,
    destinationName,
    destinationPhone,
    destinationPostalCode: destinationPostalCode?.replace('-', ''),
    destinationPrefecture: destinationPrefectureCode,
    destinationCity: destinationAddressParts.city,
    destinationAddress: destinationAddressParts.rest,
    contactorName: destinationName,
    contactorPhone: destinationPhone,
    contactorPostalCode: destinationPostalCode?.replace('-', ''),
    contactorPrefecture: destinationPrefectureCode,
    contactorCity: destinationAddressParts.city,
    contactorAddress: destinationAddressParts.rest,
    route: 'LIONS EC',
    note: slipNumber,
    deliveryDate,
    deliveryTime: deliveryTimeKey || null,
    amount: calculatedAmount,
    shipmentFee: 0,
    discountAmount: 0,
    createdAt,
    orderItems,
    productIds: orderItems.map(_ => _.productId),
  };
};

module.exports = {
  orderImportFields,
  mapOrderImportRow,
  lionsOrderImportFields,
  mapLionsOrderImportRow,
};
